/**
 * Dollars ↔ cents for product option price deltas. The API stores whole cents
 * (±$100 ceiling); the form edits a free-text dollar amount.
 */

export const OPTION_PRICE_DELTA_CENTS_MAX = 10_000;

/**
 * Parse a dollar string such as "1.5", "-0.75", "+2", "$3.00" or "" into
 * whole cents. Returns `null` for anything that is not a plain amount or that
 * carries more than two decimals. Blank means zero.
 */
export function dollarsToCents(input: string | number | null | undefined): number | null {
  if (input === null || input === undefined) return 0;
  if (typeof input === 'number') {
    if (!Number.isFinite(input)) return null;
    return Math.round(input * 100);
  }
  const raw = input.trim().replace(/^([+-])?\$/, '$1').replace(/,/g, '');
  if (raw === '' || raw === '+' || raw === '-') return 0;
  if (!/^[+-]?(\d+(\.\d{0,2})?|\.\d{1,2})$/.test(raw)) return null;
  const sign = raw.startsWith('-') ? -1 : 1;
  const [whole = '0', frac = ''] = raw.replace(/^[+-]/, '').split('.');
  const cents = Number(whole) * 100 + Number(frac.padEnd(2, '0').slice(0, 2));
  return sign * cents === 0 ? 0 : sign * cents;
}

/** Whole cents → "1.50" / "-0.75" / "0.00" (no currency symbol, for inputs). */
export function centsToDollars(cents: number | null | undefined): string {
  const value = Number.isFinite(cents as number) ? Math.trunc(cents as number) : 0;
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/** Whole cents → "+$1.50" / "−$0.75" / "" for zero (menu-style label). */
export function formatPriceDelta(cents: number | null | undefined): string {
  const value = Number.isFinite(cents as number) ? Math.trunc(cents as number) : 0;
  if (value === 0) return '';
  return `${value > 0 ? '+' : '−'}$${centsToDollars(Math.abs(value))}`;
}

export function isPriceDeltaInRange(cents: number): boolean {
  return Number.isInteger(cents) && Math.abs(cents) <= OPTION_PRICE_DELTA_CENTS_MAX;
}
